import { useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../store";
import { fetchFactoryApiResponse } from "@/redux/services/dashboardService";
import { setResponses } from "../../../redux/slices/dashboardSlice";
import { ExecutiveDashboardUrl, FactoryPathUrl } from "../../../utils/common";
import { isEqual } from "lodash";
import { useLocation } from "react-router-dom";
import { EXECUTIVE_DASHBOARD } from "@/utils/constants";

export const useFactoryDashboard = (FactoryPath: boolean, ExecutivePath: boolean, factoryApiResponse: any[]) => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const timeoutRef = useRef<any>(null);
    const intervalRef = useRef<any>(null);
    const lastDispatchedDataRef = useRef<any>(null);
    const isFactoryLocationDashboard = location.pathname.toLowerCase().includes(`/view/${EXECUTIVE_DASHBOARD}`);

    // Factory dashboard polling logic
    useEffect(() => {
        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);

        if (FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) {
            const startPolling = () => {
                intervalRef.current = setInterval(() => {
                    dispatch(fetchFactoryApiResponse());
                }, 5 * 60 * 1000); // 5 min
            };

            if (factoryApiResponse?.length > 0) {
                timeoutRef.current = setTimeout(() => {
                    dispatch(fetchFactoryApiResponse());
                    startPolling();
                }, 5 * 60 * 1000);
            } else {
                dispatch(fetchFactoryApiResponse());
                startPolling();
            }
        }

        return () => {
            clearTimeout(timeoutRef.current);
            clearInterval(intervalRef.current);
        };
    }, [dispatch, FactoryPathUrl(FactoryPath), factoryApiResponse, ExecutiveDashboardUrl(ExecutivePath)]);

    // Transform factory data
    const transformedData = useMemo(() => {
        if (!factoryApiResponse || factoryApiResponse.length === 0) return [];

        const result: any = [];

        factoryApiResponse.forEach(customerEntry => {
            const customerName = Object.keys(customerEntry)[0];
            const sitesArray = customerEntry[customerName].sites.map((site: { equipment_data: any[]; site_id: any; }) => ({
                ...site,
                requested_schema: customerName,
                equipment_data: site.equipment_data.map(equipment => ({
                    ...equipment,
                    data: equipment.data.map((dataPoint: any) => ({
                        ...dataPoint,
                        equipment_id: equipment.equipment_id,
                        site: site.site_id
                    }))
                }))
            }));

            result.push(sitesArray);
        });

        return result;
    }, [factoryApiResponse]);

    // Update responses when transformed data changes
    useEffect(() => {
        if (transformedData && !isEqual(lastDispatchedDataRef.current, transformedData)) {
            lastDispatchedDataRef.current = transformedData;
            dispatch(setResponses(transformedData));
        }
    }, [transformedData, dispatch]);

    // Set factory dashboard title
    useEffect(() => {
        if (FactoryPathUrl(FactoryPath)) {
            document.title = "Factory Dashboard: e-BoostRealM";
        }
    }, [FactoryPathUrl(FactoryPath)]);

    useEffect(() => {
        if (FactoryPathUrl(ExecutivePath)) {
            document.title = "Executive View: e-BoostRealM";
        }
    }, [FactoryPathUrl(ExecutivePath)]);

    return { transformedData };
};